/* eslint-disable react/prop-types */
import clsx from 'clsx'
import React, { useState, useRef, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PopoverStickOnHover from '../../partials/content/PopoverStickOnHover'
import { DEFAULT_ACTION_HANDLERS } from './actionDefinitions'

function PopoverMenuButton(props) {
  const {
    item,
    employee,
    context,
    onAction,
    customActionHandlers,
    showIcons = false, // Default to hiding icons
  } = props

  const {
    id,
    label,
    icon,
    action,
    onClick,
    permissionCheck,
    availabilityCheck,
    shouldRender,
    disabledPopover,
    subMenu, // Support for nested sub-menus
  } = item

  const [showSubMenu, setShowSubMenu] = useState(false)
  const [subMenuPosition, setSubMenuPosition] = useState('right')
  const menuItemRef = useRef(null)
  const subMenuRef = useRef(null)

  // Calculate sub-menu position based on available viewport space
  useEffect(() => {
    if (showSubMenu && menuItemRef.current && subMenuRef.current) {
      const menuItem = menuItemRef.current
      const rect = menuItem.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const subMenuWidth = 200 // minWidth from style
      const spaceOnRight = viewportWidth - rect.right
      const spaceOnLeft = rect.left

      // If not enough space on right, position to the left
      if (spaceOnRight < subMenuWidth && spaceOnLeft > subMenuWidth) {
        setSubMenuPosition('left')
      } else {
        setSubMenuPosition('right')
      }
    }
  }, [showSubMenu])

  if (shouldRender && !shouldRender(employee, context)) {
    return null
  }

  // Check permissions
  const hasPermission = permissionCheck ? permissionCheck(context) : true
  const availability = availabilityCheck ? availabilityCheck(employee, context) : { isAvailable: true }
  const isAvailable = availability?.isAvailable !== false

  // Note: hasExtendedCredentials check is only for special Exchange Online case (offboard-user)
  // Other actions don't check hasExtendedCredentials here - they still render but may be disabled
  const isDisabled = !hasPermission || !isAvailable

  const handleClick = () => {
    if (isDisabled) return

    // Don't trigger action if item has a sub-menu (sub-menu items handle their own actions)
    if (subMenu && subMenu.items && subMenu.items.length > 0) {
      return
    }

    // Use action ID (id property) to determine which handler to call
    const actionId = action || id

    if (onClick) {
      // Custom onClick in config takes highest precedence
      onClick(employee, context)
    } else if (actionId) {
      // Priority: customActionHandlers > onAction > defaultHandler
      if (customActionHandlers?.[actionId]) {
        customActionHandlers[actionId](employee, context)
      } else if (onAction) {
        onAction(actionId, employee, context)
      } else if (DEFAULT_ACTION_HANDLERS[actionId]) {
        // Use default handler from actionDefinitions
        DEFAULT_ACTION_HANDLERS[actionId](employee, context)
      } else {
        // Debug: log if no handler found
        console.warn(`PopoverMenu: No handler found for action "${actionId}"`, {
          availableHandlers: Object.keys(DEFAULT_ACTION_HANDLERS),
        })
      }
    }
  }

  // Get popover content for disabled state
  const getPopoverContent = () => {
    if (disabledPopover) {
      return disabledPopover
    }

    // Check for special Exchange Online case
    // INTEGRATION_TYPE.LEGACY = 0, TOKEN = 1, APP = 2
    if (
      id === 'offboard-user' &&
      context.integrationType === 'Office365' &&
      context.selectedIntegration &&
      ((context.selectedIntegration.integration_type === 0 && !context.hasExtendedCredentials) ||
        (context.selectedIntegration.integration_type === 1 && !context.hasExoCredentials) ||
        (context.selectedIntegration.integration_type === 2 && !context.hasExoCredentials))
    ) {
      return {
        title: 'ENGAGE.EXCHANGE_ONLINE_NOT_CONNECTED',
        actionButton: {
          label: 'INTEGRATION.CONNECT_EXCHANGE_ONLINE',
          onClick: context.onConnectPowershell,
        },
      }
    }

    // Default insufficient permissions popover
    if (!hasPermission) {
      return {
        title: 'ENGAGE.INSUFFICIENT_PERMISSIONS',
        actionButton: {
          label: 'INTEGRATIONS.GO_TO',
          onClick: context.navigateToIntegrations,
        },
      }
    }

    return null
  }

  const popoverContent = getPopoverContent()

  // Handle sub-menu items
  const renderSubMenuItems = () => {
    if (!subMenu || !subMenu.items || subMenu.items.length === 0) {
      return null
    }

    if (!showSubMenu) {
      return null
    }

    const positionStyle =
      subMenuPosition === 'left'
        ? {
            right: '100%',
            left: 'auto',
            marginRight: '0',
            marginLeft: '0',
          }
        : {
            left: '100%',
            right: 'auto',
            marginLeft: '0',
            marginRight: '0',
          }

    return (
      <>
        {/* Invisible bridge to prevent gap */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            ...(subMenuPosition === 'left'
              ? { right: '100%', width: '2px', height: '35px' }
              : { left: '100%', width: '2px', height: '35px' }),
            zIndex: 9999,
          }}
          onMouseEnter={() => setShowSubMenu(true)}
        />
        <div
          ref={subMenuRef}
          className="dropdown-menu dropdown-menu-fit dropdown-menu-end dropdown-menu-anim dropdown-menu-top-unround popover-submenu popover-menu-dropdown"
          style={{
            position: 'absolute',
            top: 0,
            ...positionStyle,
            zIndex: 10000,
            display: 'block',
            minWidth: '280px',
            backgroundColor: 'var(--bs-dropdown-bg)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          onMouseEnter={() => setShowSubMenu(true)}
          onMouseLeave={() => {
            // Delay closing to allow mouse to return to parent
            setTimeout(() => {
              if (!menuItemRef.current?.matches(':hover')) {
                setShowSubMenu(false)
              }
            }, 100)
          }}
        >
          <ul
            className="nav popover-menu-actions-nav"
            style={{ width: '280px', marginTop: '0.25rem', marginBottom: '0.25rem' }}
          >
            {subMenu.title && (
              <li
                className="nav__item nav__item--title fw-normal"
                style={{ padding: '0.375rem 1rem 0.25rem', fontSize: '1rem' }}
              >
                <FormattedMessage id={subMenu.title} />
              </li>
            )}
            {subMenu.items.map((subItem) => {
              const subItemConfig = typeof subItem === 'string' ? { id: subItem } : subItem
              return (
                <PopoverMenuButton
                  key={subItemConfig.id}
                  item={subItemConfig}
                  employee={employee}
                  context={context}
                  onAction={onAction}
                  customActionHandlers={customActionHandlers}
                  showIcons={showIcons}
                />
              )
            })}
          </ul>
        </div>
      </>
    )
  }

  // Render disabled item with popover
  if (isDisabled && popoverContent) {
    return (
      <PopoverStickOnHover
        component={
          <>
            <p className="p-3 pb-0" style={{ textAlign: 'left' }}>
              <FormattedMessage id={popoverContent.title} />
            </p>
            {popoverContent.actionButton && (
              <Button
                variant="link"
                onClick={() => {
                  if (popoverContent.actionButton.onClick) {
                    popoverContent.actionButton.onClick(context)
                  }
                }}
              >
                <FormattedMessage id={popoverContent.actionButton.label} />
              </Button>
            )}
          </>
        }
        placement="left-start"
        onMouseEnter={() => {}}
        delay={200}
      >
        <li className="nav__item nav__item--disabled" data-bs-toggle="collapse" style={{ padding: 0, margin: 0 }}>
          <span
            className="nav__link"
            style={{
              padding: '0.5rem 1rem',
              height: '35px',
              lineHeight: '35px',
              minHeight: '35px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
            <span className="nav__link nav__link-text">
              <FormattedMessage id={label} />
            </span>
            {subMenu && <i className="fa fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '0.75rem' }} />}
          </span>
        </li>
      </PopoverStickOnHover>
    )
  }

  // Render item with sub-menu
  if (subMenu && subMenu.items && subMenu.items.length > 0) {
    return (
      <li
        ref={menuItemRef}
        className={clsx('nav__item', {
          'nav__item--disabled': isDisabled,
        })}
        data-bs-toggle="collapse"
        style={{ position: 'relative', padding: 0, margin: 0 }}
        onMouseEnter={() => setShowSubMenu(true)}
        onMouseLeave={(e) => {
          // Use setTimeout to allow mouse to move to sub-menu
          const { relatedTarget } = e
          if (!relatedTarget || !relatedTarget.closest('.popover-submenu')) {
            setTimeout(() => {
              if (!document.querySelector('.popover-submenu:hover') && !menuItemRef.current?.matches(':hover')) {
                setShowSubMenu(false)
              }
            }, 100)
          }
        }}
      >
        <span
          className="nav__link"
          style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 1rem', height: '35px', minHeight: '35px' }}
        >
          {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
          <span className="nav__link nav__link-text">
            <FormattedMessage id={label} />
          </span>
          <i className="fa fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '0.75rem' }} />
        </span>
        {renderSubMenuItems()}
      </li>
    )
  }

  // Render enabled or disabled (without popover) item
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      onClick={handleClick}
      className={clsx('nav__item', {
        'nav__item--disabled': isDisabled,
      })}
      data-bs-toggle="collapse"
      style={{ padding: 0, margin: 0 }}
    >
      <span
        className="nav__link"
        style={{
          padding: '0.5rem 1rem',
          height: '35px',
          lineHeight: '35px',
          minHeight: '35px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
        <span className="nav__link nav__link-text">
          <FormattedMessage id={label} />
        </span>
      </span>
    </li>
  )
}

export default PopoverMenuButton
